'use client';
import { Text } from '@mnd-frontend/ui/publish/components';
import { SPACING_1, SPACING_1_5, SPACING_4 } from '@mnd-frontend/ui/shared/tokens';
import styled from 'styled-components';
import { Card } from './Card';
import { TrendPill } from './TrendPill';

const StyledMetrics = styled(Card)`
  display: flex;
  gap: ${SPACING_4};
  flex-direction: column;
`;

type DeltaDirection = 'positive' | 'negative' | 'neutral';

const FlexCenter = styled.div`
  display: flex;
  align-items: center;
`;

const Header = styled(FlexCenter)`
  justify-content: space-between;
  gap: ${SPACING_1};
`;

const Title = styled(FlexCenter)`
  gap: ${SPACING_1_5};
  p {
    word-break: break-word;
  }
`;

const Body = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: end;
`;

const BrandColor = styled.span`
  color: var(--coverage-brand-color);
`;

export const MetricCard = ({
  icon,
  title,
  value,
  unit,
  yearlyTrendDelta,
  yearlyTrendDeltaDirection,
  disclaimer,
  valueFormatter,
}: {
  icon: React.ReactNode;
  title: string;
  value: number;
  unit?: string;
  yearlyTrendDelta?: string;
  yearlyTrendDeltaDirection?: DeltaDirection;
  disclaimer?: string;
  valueFormatter: (val: number) => string;
}) => {
  const hasYearlyTrendDelta =
    yearlyTrendDelta !== undefined && yearlyTrendDeltaDirection !== undefined;
  return (
    <StyledMetrics>
      <Header>
        <Title>
          <BrandColor>{icon}</BrandColor>
          <Text
            as="p"
            mobileVariant="reportLabelMedium"
            variant="reportHeadlineSmall"
            color="GREY_900"
          >
            {title}
          </Text>
        </Title>
        {hasYearlyTrendDelta && yearlyTrendDeltaDirection !== 'neutral' && (
          <TrendPill direction={yearlyTrendDeltaDirection} value={yearlyTrendDelta} />
        )}
      </Header>
      <Body>
        <Text
          as="p"
          variant="reportDisplayMedium"
          mobileVariant="reportHeadlineLarge"
          color="GREY_900"
        >
          {valueFormatter(value)}
        </Text>
        {unit && (
          <Text as="p" variant="reportLabelSmall" color="GREY_900">
            {unit}
          </Text>
        )}
      </Body>
      {disclaimer && (
        <Text as="p" variant="reportLabelSmall" color="GREY_600">
          {disclaimer}
        </Text>
      )}
    </StyledMetrics>
  );
};
